import { useIntl } from 'react-intl'
import Pagination from './Pagination'
import { currency } from '../helpers/currency'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useIsMobile } from '../hook/useIsMobile'

export interface ColumnType {
  title: string
  tooltip?: string
  dataIndex: string
  width?: string
  key: string
  render?: (
    text: any,
    data: ColumnType,
    index: number,
    packageName?: string,
    packageId?: string,
    isPaid?: boolean
  ) => JSX.Element
  status?: boolean
  yesOrNo?: boolean
  send?: boolean
  storeStatus?: boolean
  dropdown?: any[]
  monetize?: boolean
  customMonetize?: boolean
  mailSent?: boolean
}

interface TableProps {
  loading?: boolean
  tableName?: string
  selectedRowKeys?: string[]
  setSelectedRowKeys?: (keys: any[]) => void
  columns: ColumnType[]
  dataSource: any
  pagination?: {
    current: number
    pageSize: number
    total: number
    onChange: (pagination: { current: number; pageSize: number; total: number }) => void
  }
}

export function Table({
  loading,
  tableName,
  selectedRowKeys,
  setSelectedRowKeys,
  columns,
  dataSource,
  pagination,
}: TableProps) {
  const intl = useIntl()
  const isMobile = useIsMobile()

  const sendCheck = (input: any) => {
    const activeTex = intl.formatMessage({ id: 'SEND' })
    const passiveText = intl.formatMessage({ id: 'NOT_SEND' })

    let text = passiveText
    let boolen = true

    switch (input) {
      case '1':
        text = activeTex
        boolen = true
        break
      case '0':
        text = passiveText
        boolen = false
        break
      case 2:
        text = activeTex
        boolen = true
        break
      case 1:
        text = activeTex
        boolen = true
        break
      case 0:
        text = passiveText
        boolen = false
        break
    }

    return { boolen, text }
  }

  const mailSentCheck = (input: any) => {
    const activeTex = intl.formatMessage({ id: 'SEND' })
    const pendingText = intl.formatMessage({ id: 'PENDING' })
    const scheduledText = intl.formatMessage({ id: 'SCHEDULED' })
    const passiveText = intl.formatMessage({ id: 'NOT_SEND' })

    let text = passiveText
    let boolen = true
    let color = 'danger'

    switch (input) {
      case 1:
        text = pendingText
        boolen = true
        color = 'warning'
        break
      case 2:
        text = activeTex
        boolen = false
        color = 'success'
        break
      case 3:
        text = passiveText
        boolen = false
        color = 'danger'
        break
      case 4:
        text = scheduledText
        boolen = true
        color = 'info'
        break
      case 0:
        text = passiveText
        boolen = false
        color = 'danger'
        break
    }

    return { boolen, text, color }
  }

  const statusCheck = (input: any) => {
    const activeTex = intl.formatMessage({ id: 'ACTIVE' })
    const passiveText = intl.formatMessage({ id: 'PASSIVE' })
    const inactiveText = intl.formatMessage({ id: 'INACTIVE' })
    const loyaltyEnabled = intl.formatMessage({ id: 'LOYALTY_ENABLED' })
    const loyaltyDisabled = intl.formatMessage({ id: 'LOYALTY_DISABLED' })

    let text = passiveText
    let boolen = true

    switch (input) {
      case true:
        text = activeTex
        boolen = true
        break
      case false:
        text = passiveText
        boolen = false
        break
      case activeTex:
        text = activeTex
        boolen = true
        break
      case passiveText:
        text = passiveText
        boolen = false
        break
      case '1':
        text = activeTex
        boolen = true
        break
      case '2':
        text = passiveText
        boolen = false
        break
      case '3':
        text = inactiveText
        boolen = false
        break
      case '4':
        text = passiveText
        boolen = false
        break
      case 1:
        text = activeTex
        boolen = true
        break
      case 2:
        text = inactiveText
        boolen = false
        break
      case 3:
        text = passiveText
        boolen = false
        break
      case 'Loyalty Enabled':
        text = loyaltyEnabled
        boolen = true
        break
      case 'Loyalty Disabled':
        text = loyaltyDisabled
        boolen = false
        break
    }

    return { boolen, text }
  }

  const yesOrNoCheck = (input: any) => {
    const activeTex = intl.formatMessage({ id: 'YES' })
    const passiveText = intl.formatMessage({ id: 'NO' })

    let text = passiveText
    let boolen = true

    switch (input) {
      case true:
        text = activeTex
        boolen = true
        break
      case false:
        text = passiveText
        boolen = false
        break
    }

    return { boolen, text }
  }

  const storeStatusCheck = (input: any) => {
    const activeTex = intl.formatMessage({ id: 'Active' })
    const passiveText = intl.formatMessage({ id: 'Passive' })
    const inactiveText = intl.formatMessage({ id: 'Inactive' })

    let text = passiveText
    let boolen = true

    switch (input) {
      case 1:
        text = activeTex
        boolen = true
        break
      case 2:
        text = inactiveText
        boolen = false
        break
      case 3:
        text = passiveText
        boolen = false
        break
    }

    return { boolen, text }
  }

  return (
    <div style={{ minHeight: 0 }}>
      <div style={{ position: 'relative', overflowY: 'auto', width: '100%' }}>
        <table
          className='table align-middle table-row-dashed table-striped fs-6 gy-5 table-mobile-compatible'
          id='kt_customers_table'
          data-tname={tableName}
        >
          <thead>
            <tr className='text-start fw-bold fs-7 gs-0'>
              {setSelectedRowKeys && (
                <th className='w-10px pe-2'>
                  <div className='form-check form-check-sm form-check-custom form-check-solid me-3'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      data-kt-check='true'
                      data-kt-check-target='#kt_customers_table .form-check-input'
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRowKeys(dataSource.map((row) => row.id))
                        } else {
                          setSelectedRowKeys([])
                        }
                      }}
                    />
                  </div>
                </th>
              )}
              {columns.map((column, indexColumn) => {
                return (
                  <th
                    className={
                      column.key === 'EarnedPoints' || column.key === 'RedemeedPoints'
                        ? 'bg-danger border'
                        : ''
                    }
                    key={indexColumn}
                    data-key={column.dataIndex}
                    style={{
                      width: column.monetize || column.customMonetize ? '250px' : 'auto',
                      maxWidth: column.width ? column.width + 'px !important' : 'auto',
                      textAlign: column.monetize || column.customMonetize ? 'right' : 'left',
                      paddingRight: column.monetize || column.customMonetize ? '100px' : '0',
                    }}
                  >
                    {column.title}
                    {column.tooltip && (
                      <span className='position-relative'>
                        <OverlayTrigger placement='top' overlay={<Tooltip>{column.tooltip}</Tooltip>}>
                          <i className='bi bi-info-circle text-primary ms-1'></i>
                        </OverlayTrigger>
                      </span>
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          {!loading && (
            <tbody className='fw-semibold text-gray-600'>
              {dataSource?.slice(0, pagination?.pageSize || 10).map((row, rowIndex) => (
                <tr key={rowIndex} className='text-start'>
                  {selectedRowKeys && setSelectedRowKeys && (
                    <td className=''>
                      <div className='form-check form-check-sm form-check-custom form-check-solid'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRowKeys([...selectedRowKeys, row.id])
                            } else {
                              setSelectedRowKeys(selectedRowKeys.filter((key) => key !== row.id))
                            }
                          }}
                        />
                      </div>
                    </td>
                  )}
                  {columns.map((column, indexColumn) => {
                    return column.render ? (
                      <td
                        data-label={column.title}
                        key={indexColumn}
                        style={{
                          maxWidth: column.width ? column.width + 'px' : 'auto',
                        }}
                      >
                        {column.render(row[column.dataIndex], row, rowIndex)}
                      </td>
                    ) : column.status ? (
                      <td key={indexColumn} data-label={column.title}>
                        <span
                          className={`badge badge-light-${statusCheck(row[column.dataIndex]).boolen ? 'success' : 'danger'
                            } fw-bolder`}
                        >
                          {statusCheck(row[column.dataIndex]).text}
                        </span>
                      </td>
                    ) : column.storeStatus ? (
                      <td key={indexColumn} data-label={column.title}>
                        <span
                          className={`badge badge-light-${storeStatusCheck(row[column.dataIndex]).boolen ? 'success' : 'danger'
                            } fw-bolder`}
                        >
                          {storeStatusCheck(row[column.dataIndex]).text}
                        </span>
                      </td>
                    ) : column.yesOrNo ? (
                      <td key={indexColumn} data-label={column.title}>
                        <span
                          className={`badge badge-light-${yesOrNoCheck(row[column.dataIndex]).boolen ? 'success' : 'danger'
                            } fw-bolder`}
                        >
                          {yesOrNoCheck(row[column.dataIndex]).text}
                        </span>
                      </td>
                    ) : column.send ? (
                      <td key={indexColumn} data-label={column.title}>
                        <span
                          className={`badge badge-light-${sendCheck(row[column.dataIndex]).boolen ? 'success' : 'danger'
                            } fw-bolder`}
                        >
                          {sendCheck(row[column.dataIndex]).text}
                        </span>
                      </td>
                    ) : column.mailSent ? (
                      <td key={indexColumn} data-label={column.title}>
                        <span
                          className={`badge badge-light-${mailSentCheck(row[column.dataIndex]).color
                            }`}
                        >
                          {mailSentCheck(row[column.dataIndex]).text}
                        </span>
                      </td>
                    ) : column.monetize ? (
                      <td key={indexColumn} style={{ textAlign: isMobile ? 'left' : 'right', paddingRight: isMobile ? 0 : '100px' }} data-label={column.title}>
                        {currency(row[column.dataIndex])}
                      </td>
                    ) : column.customMonetize ? (
                      <td key={indexColumn} style={{ textAlign: isMobile ? 'left' : 'right', paddingRight: isMobile ? 0 : '100px' }} data-label={column.title}>
                        {row.type === 'Refund'
                          ? currency((row?.totalAmount - row?.discountAmount) * -1 || 0)
                          : row.type === 'Birthday' || row.type === 'SignUp'
                            ? '-'
                            : currency(row[column.dataIndex] || 0)}
                      </td>
                    ) : (
                      <td key={indexColumn} data-label={column.title}>{row[column.dataIndex]}</td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          )}
        </table>

        {!loading && dataSource?.length === 0 && (
          <div
            style={{
              top: 0,
              left: 0,
              height: '100%',
              width: '100%',
              zIndex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {intl.formatMessage({ id: 'NOT_FOUND' })}
          </div>
        )}

        {loading && (
          <div
            style={{
              top: 0,
              left: 0,
              height: '100%',
              width: '100%',
              zIndex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {intl.formatMessage({ id: 'LOADING' })}...
          </div>
        )}
      </div>
      {pagination && (
        <div
          className='d-flex flex-column flex-lg-row align-items-center justify-content-between py-3 gap-2 mt-lg-4'
          style={{ background: 'none', boxShadow: 'none', marginTop: 0 }}
        >
          <div className='d-flex flex-column flex-lg-row align-items-center gap-4 w-100'>
            <select
              style={{ width: 100, minWidth: 80, marginBottom: 4 }}
              className='form-select text-center'
              value={pagination.pageSize}
              onChange={(e) => {
                pagination.onChange({
                  ...pagination,
                  pageSize: parseInt(e.target.value),
                })
              }}
            >
              <option value='5'>5</option>
              <option value='10'>10</option>
              <option value='20'>20</option>
              <option value='30'>30</option>
              <option value='50'>50</option>
            </select>
            <div style={{ fontSize: 13, color: '#6c757d', textAlign: 'center', whiteSpace: 'nowrap' }}
            >
              {intl.formatMessage({ id: 'SHOWING' })}{' '}
              {(pagination.current - 1) * pagination.pageSize == 0
                ? 1
                : (pagination.current - 1) * pagination.pageSize}{' '}
              {intl.formatMessage({ id: 'TO' })}{' '}
              {pagination.current * pagination.pageSize > pagination.total
                ? pagination.total
                : pagination.current * pagination.pageSize}{' '}
              {intl.formatMessage({ id: 'OF' })} {pagination.total}{' '}
              {intl.formatMessage({ id: 'RECORDS' })}
            </div>
          </div>
          <div className='w-100 d-flex justify-content-center justify-content-lg-end'>
            <Pagination
              currentPage={pagination.current || 1}
              totalPages={Math.ceil(pagination.total / pagination.pageSize) || 1}
              setCurrentPage={(page) => {
                pagination.onChange({ ...pagination, current: page })
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
